import * as Sentry from "@sentry/vue";

function lazyLoadSentryIntegrations() {
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    });
}

function getSentryIntegrations() {
    const router = useRouter();
    const browserTracing = Sentry.browserTracingIntegration({
      router,
    });
    return [browserTracing];
}

export default defineNuxtPlugin({
    name: 'sentry',
    parallel: true,
    async setup(nuxtApp) {
        const vueApp = nuxtApp.vueApp;
        const config = useRuntimeConfig();

        Sentry.init({
            app: vueApp,
            dsn: config.public.GLITCHTIP_DSN_PUBLIC ?? null,
            release: config.public.GLITCHTIP_RELEASE || undefined,
            integrations: getSentryIntegrations(),

            tracesSampleRate: config.public.GLITCHTIP_TRACES_SAMPLE_RATE as number,
            replaysSessionSampleRate: config.public.GLITCHTIP_REPLAY_SAMPLE_RATE as number,
            replaysOnErrorSampleRate: config.public.GLITCHTIP_ERROR_REPLAY_SAMPLE_RATE as number,

            // Keep envelope POSTs out of the critical request chain.
            transportOptions: {
                fetchOptions: {
                    keepalive: true,
                    priority: "low",
                },
            },

            beforeSend(event) {
                const ex = event.exception?.values?.[0];
                const frames = ex?.stacktrace?.frames ?? [];
                const lastFrame = frames[frames.length - 1]?.filename ?? '';
                const msg = ex?.value ?? '';
                const fnNames = frames.map((f) => f.function ?? '').join(' ');

                // Browser extensions - any frame from a chrome-extension:// origin.
                if (frames.some((f) => /^chrome-extension:\/\//.test(f.filename ?? ''))) {
                    return null;
                }

                // 3rd-party trackers / chat widgets.
                const thirdParty = [
                    /capture_configs\/a11\/cbb\/790/,
                    /rlets\.com/,
                    /le_secure_storage/,
                    /liveperson/i,
                    /a2684c87-8205-4017-a97a-b0fbb78e35d2/,
                    /LocalIQWidgets/,
                ];
                if (thirdParty.some((re) => re.test(lastFrame) || re.test(msg))) {
                    return null;
                }

                // iOS in-app webview clients calling native bridges we don't ship.
                if (/window\.webkit\.messageHandlers/.test(msg) && /sendDataToNative/.test(fnNames)) {
                    return null;
                }

                // Safari <=15.3 web-vitals polyfill gap.
                if (/web-vitals/.test(lastFrame) && /\.at is not a function/.test(msg)) {
                    return null;
                }

                return event;
            },
        });

        lazyLoadSentryIntegrations();
    }
});
